const DEBUG_ID = null
class Color {
    constructor(r, g, b) {
        this.set(r, g, b);
    }

    toRgb() {
        return `rgb(${Math.round(this.r)}, ${Math.round(this.g)}, ${Math.round(this.b)})`;
    }

    toHex() {
        return rgbToHex(Math.round(this.r), Math.round(this.g), Math.round(this.b));
    }

    set(r, g, b) {
        this.r = this.clamp(r);
        this.g = this.clamp(g);
        this.b = this.clamp(b);
    }

    hueRotate(angle = 0) {
        angle = angle / 180 * Math.PI;
        const sin = Math.sin(angle);
        const cos = Math.cos(angle);

        this.multiply([
            0.213 + cos * 0.787 - sin * 0.213,
            0.715 - cos * 0.715 - sin * 0.715,
            0.072 - cos * 0.072 + sin * 0.928,
            0.213 - cos * 0.213 + sin * 0.143,
            0.715 + cos * 0.285 + sin * 0.140,
            0.072 - cos * 0.072 - sin * 0.283,
            0.213 - cos * 0.213 - sin * 0.787,
            0.715 - cos * 0.715 + sin * 0.715,
            0.072 + cos * 0.928 + sin * 0.072,
        ]);
    }

    grayscale(value = 1) {
        this.multiply([
            0.2126 + 0.7874 * (1 - value),
            0.7152 - 0.7152 * (1 - value),
            0.0722 - 0.0722 * (1 - value),
            0.2126 - 0.2126 * (1 - value),
            0.7152 + 0.2848 * (1 - value),
            0.0722 - 0.0722 * (1 - value),
            0.2126 - 0.2126 * (1 - value),
            0.7152 - 0.7152 * (1 - value),
            0.0722 + 0.9278 * (1 - value),
        ]);
    }

    sepia(value = 1) {
        this.multiply([
            0.393 + 0.607 * (1 - value),
            0.769 - 0.769 * (1 - value),
            0.189 - 0.189 * (1 - value),
            0.349 - 0.349 * (1 - value),
            0.686 + 0.314 * (1 - value),
            0.168 - 0.168 * (1 - value),
            0.272 - 0.272 * (1 - value),
            0.534 - 0.534 * (1 - value),
            0.131 + 0.869 * (1 - value),
        ]);
    }

    saturate(value = 1) {
        this.multiply([
            0.213 + 0.787 * value,
            0.715 - 0.715 * value,
            0.072 - 0.072 * value,
            0.213 - 0.213 * value,
            0.715 + 0.285 * value,
            0.072 - 0.072 * value,
            0.213 - 0.213 * value,
            0.715 - 0.715 * value,
            0.072 + 0.928 * value,
        ]);
    }

    multiply(matrix) {
        const newR = this.clamp(this.r * matrix[0] + this.g * matrix[1] + this.b * matrix[2]);
        const newG = this.clamp(this.r * matrix[3] + this.g * matrix[4] + this.b * matrix[5]);
        const newB = this.clamp(this.r * matrix[6] + this.g * matrix[7] + this.b * matrix[8]);
        this.r = newR;
        this.g = newG;
        this.b = newB;
    }

    brightness(value = 1) {
        this.linear(value);
    }
    contrast(value = 1) {
        this.linear(value, -(0.5 * value) + 0.5);
    }

    linear(slope = 1, intercept = 0) {
        this.r = this.clamp(this.r * slope + intercept * 255);
        this.g = this.clamp(this.g * slope + intercept * 255);
        this.b = this.clamp(this.b * slope + intercept * 255);
    }

    invert(value = 1) {
        this.r = this.clamp((value + this.r / 255 * (1 - 2 * value)) * 255);
        this.g = this.clamp((value + this.g / 255 * (1 - 2 * value)) * 255);
        this.b = this.clamp((value + this.b / 255 * (1 - 2 * value)) * 255);
    }

    hsl() {
        // Code taken from https://stackoverflow.com/a/9493060/2688027, licensed under CC BY-SA.
        const r = this.r / 255;
        const g = this.g / 255;
        const b = this.b / 255;
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h, s, l = (max + min) / 2;

        if (max === min) {
            h = s = 0;
        } else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;

                case g:
                    h = (b - r) / d + 2;
                    break;

                case b:
                    h = (r - g) / d + 4;
                    break;
            }
            h /= 6;
        }

        return {
            h: h * 100,
            s: s * 100,
            l: l * 100,
        };
    }

    clamp(value) {
        if (value > 255) {
            value = 255;
        } else if (value < 0) {
            value = 0;
        }
        return value;
    }
}

class Solver {
    constructor(target, baseColor) {
        this.target = target;
        this.targetHSL = target.hsl();
        this.reusedColor = new Color(0, 0, 0);
    }

    solve() {
        const result = this.solveNarrow(this.solveWide());
        return {
            values: result.values,
            loss: result.loss,
            filter: this.css(result.values),
            filterRaw: this.raw(result.values),
        };
    }

    solveWide() {
        const A = 5;
        const c = 15;
        const a = [60, 180, 18000, 600, 1.2, 1.2];

        let best = { loss: Infinity };
        for (let i = 0; best.loss > 25 && i < 3; i++) {
            const initial = [50, 20, 3750, 50, 100, 100];
            const result = this.spsa(A, a, c, initial, 1000);
            if (result.loss < best.loss) {
                best = result;
            }
        }
        return best;
    }

    solveNarrow(wide) {
        const A = wide.loss;
        const c = 2;
        const A1 = A + 1;
        const a = [0.25 * A1, 0.25 * A1, A1, 0.25 * A1, 0.2 * A1, 0.2 * A1];
        return this.spsa(A, a, c, wide.values, 500);
    }

    spsa(A, a, c, values, iters) {
        const alpha = 1;
        const gamma = 0.16666666666666666;

        let best = null;
        let bestLoss = Infinity;
        const deltas = new Array(6);
        const highArgs = new Array(6);
        const lowArgs = new Array(6);

        for (let k = 0; k < iters; k++) {
            const ck = c / Math.pow(k + 1, gamma);
            for (let i = 0; i < 6; i++) {
                deltas[i] = Math.random() > 0.5 ? 1 : -1;
                highArgs[i] = values[i] + ck * deltas[i];
                lowArgs[i] = values[i] - ck * deltas[i];
            }

            const lossDiff = this.loss(highArgs) - this.loss(lowArgs);
            for (let i = 0; i < 6; i++) {
                const g = lossDiff / (2 * ck) * deltas[i];
                const ak = a[i] / Math.pow(A + k + 1, alpha);
                values[i] = fix(values[i] - ak * g, i);
            }

            const loss = this.loss(values);
            if (loss < bestLoss) {
                best = values.slice(0);
                bestLoss = loss;
            }
        }
        return { values: best, loss: bestLoss };

        function fix(value, idx) {
            let max = 100;
            if (idx === 2 /* saturate */) {
                max = 7500;
            } else if (idx === 4 /* brightness */ || idx === 5 /* contrast */) {
                max = 200;
            }

            if (idx === 3 /* hue-rotate */) {
                if (value > max) {
                    value %= max;
                } else if (value < 0) {
                    value = max + value % max;
                }
            } else if (value < 0) {
                value = 0;
            } else if (value > max) {
                value = max;
            }
            return value;
        }
    }

    loss(filters) {
        // Argument is array of percentages.
        const color = this.reusedColor;
        color.set(0, 0, 0);

        color.invert(filters[0] / 100);
        color.sepia(filters[1] / 100);
        color.saturate(filters[2] / 100);
        color.hueRotate(filters[3] * 3.6);
        color.brightness(filters[4] / 100);
        color.contrast(filters[5] / 100);

        const colorHSL = color.hsl();
        return (
            Math.abs(color.r - this.target.r) +
            Math.abs(color.g - this.target.g) +
            Math.abs(color.b - this.target.b) +
            Math.abs(colorHSL.h - this.targetHSL.h) +
            Math.abs(colorHSL.s - this.targetHSL.s) +
            Math.abs(colorHSL.l - this.targetHSL.l)
        );
    }

    raw(filters) {
        function fmt(idx, multiplier = 1) {
            return Math.round(filters[idx] * multiplier);
        }
        return `brightness(0) saturate(100%) invert(${fmt(0)}%) sepia(${fmt(1)}%) saturate(${fmt(2)}%) hue-rotate(${fmt(3, 3.6)}deg) brightness(${fmt(4)}%) contrast(${fmt(5)}%)`;
    }

    css(filters) {
        function fmt(idx, multiplier = 1) {
            return Math.round(filters[idx] * multiplier);
        }
        return `filter: brightness(0) saturate(100%) invert(${fmt(0)}%) sepia(${fmt(1)}%) saturate(${fmt(2)}%) hue-rotate(${fmt(3, 3.6)}deg) brightness(${fmt(4)}%) contrast(${fmt(5)}%);`;
    }
}

function hexToRgb(hexColor) {
    // Remove hash character, if present
    hexColor = hexColor.replace('#', '');

    // Convert 3-digit hex to 6-digit hex
    if (hexColor.length === 3) {
        hexColor = hexColor
            .split('')
            .map((char) => char + char)
            .join('');
    }

    // Convert hex to RGB
    const red = parseInt(hexColor.substring(0, 2), 16);
    const green = parseInt(hexColor.substring(2, 4), 16);
    const blue = parseInt(hexColor.substring(4, 6), 16);

    return { red, green, blue };
}


const HSLStringToHex = function (hslString) {
    if (hslString[0] != 'h') {
        return hslString;
    }
    // Remove 'hsl(' and ')' from the input string
    var hslValues = hslString.substring(4, hslString.length - 1).split(',');

    // Extract H, S, and L values
    var hue = parseInt(hslValues[0].trim(), 10);
    var saturation = parseInt(hslValues[1].trim(), 10);
    var lightness = parseInt(hslValues[2].trim(), 10);

    let [r, g, b] = HSLToRGB(hue, saturation, lightness);
    return RGBToHEX(r, g, b);
}

const componentToHex = (c) => {
    // Max is 255 and min is 0
    c = Math.min(Math.max(0, c), 255);
    c = Math.round(c);
    const hex = c.toString(16);
    return hex.length == 1 ? "0" + hex : hex;
}

const RGBToHEX = (r, g, b) => {
    return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
}

const HSLToRGB = (h, s, l) => {
    var r, g, b;
    h /= 360;
    s /= 100;
    l /= 100;

    if (s == 0) {
        r = g = b = l; // achromatic
    } else {
        function hue2rgb(p, q, t) {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        }

        var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        var p = 2 * l - q;

        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    return [r * 255, g * 255, b * 255];
}

const RGBToHSL = (r, g, b) => {
    r /= 255;
    g /= 255;
    b /= 255;
    const l = Math.max(r, g, b);
    const s = l - Math.min(r, g, b);
    const h = s
        ? l === r
            ? (g - b) / s
            : l === g
                ? 2 + (b - r) / s
                : 4 + (r - g) / s
        : 0;
    return [
        60 * h < 0 ? 60 * h + 360 : 60 * h,
        100 * (s ? (l <= 0.5 ? s / (2 * l - s) : s / (2 - (2 * l - s))) : 0),
        (100 * (2 * l - s)) / 2,
    ];
};

function HEXtoHSL(hex) {
    const { red, green, blue } = hexToRgb(hex);
    return RGBToHSL(red, green, blue);
}

function HSLtoHEX(hsl) {
    const [h, s, l] = hsl;
    const [r, g, b] = HSLToRGB(h, s, l);
    return RGBToHEX(r, g, b);
}

function colorToHSL(color) {
    let hsl = null
    if (color[0] == 'r') {
        let rgb = color.replace('rgb(', '').replace(')', '').split(',').map((v) => parseFloat(v))
        hsl = RGBToHSL(rgb[0], rgb[1], rgb[2])
    } else {
        hsl = HEXtoHSL(color)
    }
    return hsl
}

function colorToHex(color) {
    if (color[0] == '#') {
        return color;
    } else if (color[0] == 'r') {
        let rgb = color.replace('rgb(', '').replace(')', '').split(',').map((v) => parseFloat(v))
        return RGBToHEX(rgb[0], rgb[1], rgb[2]);
    }
    return null
}

function HUERotate(color, angle) {
    let hsl = colorToHSL(color)
    hsl[0] = (hsl[0] + angle) % 360
    return HSLtoHEX(hsl)
}

function TransformColor(color, angle, saturation) {
    let hsl = colorToHSL(color)
    hsl[0] = (hsl[0] + angle) % 360
    if (saturation) {
        hsl[1] = Math.min(100, Math.max(0, hsl[1]*saturation))
    }
    return HSLtoHEX(hsl)
}

function setLuminance(color, luminance) {
    let [h, s, l] = [0, 0, 0];
    if (color[0] == '#') {
        const { red, green, blue } = hexToRgb(color);
        [h, s, l] = RGBToHSL(red, green, blue);
    } else if (color[0] == 'h') {
        [h, s, l] = color.replace('hsl(', '').replace(')', '').split(',').map((v) => parseFloat(v));
    }
    const [r, g, b] = HSLToRGB(h, s, luminance);
    return RGBToHEX(r, g, b);
}

function mergeWithBlack(color, weight) {
    let colorRGB = hexToRgb(color)
    color = [colorRGB.red, colorRGB.green, colorRGB.blue]
    const colorDec = color.map(value => value / 255);
    const blackDec = [0, 0, 0];
    const mergedColor = colorDec.map((value, index) => (value * (1 - weight)) + (blackDec[index] * weight));
    const mergedColorRgb = mergedColor.map(value => Math.round(value * 255));

    return RGBToHEX(mergedColorRgb[0], mergedColorRgb[1], mergedColorRgb[2]);
}

function mergeWithWhite(color, weight) {
    let colorRGB = hexToRgb(color)
    color = [colorRGB.red, colorRGB.green, colorRGB.blue]
    const colorDec = color.map(value => value / 255);
    const whiteDec = [1, 1, 1];
    const mergedColor = colorDec.map((value, index) => (value * (1 - weight)) + (whiteDec[index] * weight));
    const mergedColorRgb = mergedColor.map(value => Math.round(value * 255));

    return RGBToHEX(mergedColorRgb[0], mergedColorRgb[1], mergedColorRgb[2]);
}

function makeColorDarker(color, amount) {
    if (!amount) {
        amount = 5
    }
    const [h, s, l] = HEXtoHSL(color);
    let newL = l + amount;
    if (newL < 0) newL = 0;
    if (newL > 100) newL = 100;
    return HSLtoHEX([h, s, newL]);
}

function makeColorLighter(color, amount) {
    return makeColorDarker(color, -amount);
}

function adjustLightness(color, percent=0.0) {
    const { red, green, blue } = hexToRgb(color);
    const [h, s, l] = RGBToHSL(red, green, blue);
    const [r, g, b] = HSLToRGB(h, s, l + percent);
    return RGBToHEX(r, g, b);
}

function improveLuminance(color, targetLuminance) {
    const { red, green, blue } = hexToRgb(color);
    [h, s, l] = RGBToHSL(red, green, blue);

    if (l > 50) {
        return null
    }
    return setLuminance(color, targetLuminance);
}

function getHEXRelativeLuminance(hexColor) {
    const { red, green, blue } = hexToRgb(hexColor);
    return getRelativeLuminance({ red, green, blue });
}

function getRelativeLuminance(rgbColor) {
    const { red, green, blue } = rgbColor;

    const sRGB = [red, green, blue].map((value) => {
        const sRGBValue = value / 255;
        return sRGBValue <= 0.03928 ? sRGBValue / 12.92 : Math.pow((sRGBValue + 0.055) / 1.055, 2.4);
    });

    const relativeLuminance = 0.2126 * sRGB[0] + 0.7152 * sRGB[1] + 0.0722 * sRGB[2];

    return relativeLuminance;
}

function getContrastRatio(hexColor1, hexColor2) {
    // Convert hex colors to RGB
    const color1 = hexToRgb(hexColor1);
    const color2 = hexToRgb(hexColor2);

    // Calculate relative luminance
    const luminance1 = getRelativeLuminance(color1);
    const luminance2 = getRelativeLuminance(color2);

    // Calculate contrast ratio
    const contrastRatio = (Math.max(luminance1, luminance2) + 0.05) / (Math.min(luminance1, luminance2) + 0.05);

    return contrastRatio;
}

function getContrastColor(color, minContrast = 4, forceLighter = null) {
    let colorRGB = hexToRgb(color)
    let hsl = RGBToHSL(colorRGB.red, colorRGB.green, colorRGB.blue)

    // TASK [HP] - Make it fail safe
    let shouldMakeLighter = forceLighter != null ? forceLighter : hsl[2] < 50

    // Loop until we get a color with enough contrast by making it lighter or darker
    let contrast = 0
    let updatedRGB = []
    let maxTries = 100
    while (contrast < minContrast || (hsl[2] < 1 || hsl[2] >= 99)) {
        if (shouldMakeLighter) {
            hsl[2] += 1
        } else {
            hsl[2] -= 1
        }
        updatedRGB = HSLToRGB(hsl[0], hsl[1], hsl[2])
        contrast = getContrastRatio(color, RGBToHEX(updatedRGB[0], updatedRGB[1], updatedRGB[2]))
        if (maxTries-- < 0) {
            break
        }
    }
    return RGBToHEX(updatedRGB[0], updatedRGB[1], updatedRGB[2])
}

function improveContrast(main, text, minContrast = 4, forceLighter = null) {
    let contrast = getContrastRatio(main, text)
    if (contrast >= minContrast) {
        return {
            main,
            text
        }
    }
    let contrastColor = getContrastColor(main, minContrast, forceLighter)
    return {
        main,
        text: contrastColor
    }
}

function improvePallete(pallete, type = 'dark') {
    let previousPallete = JSON.parse(JSON.stringify(pallete))

    let [h, s, l] = HEXtoHSL(pallete.main)
    let factorMerge = 0.4
    if (l > 80) {
        factorMerge = 0.5
    }

    let mainColor = null
    if (type == 'dark') {
        mainColor = mergeWithBlack(pallete.main, factorMerge)
    } else {
        mainColor = mergeWithWhite(pallete.main, factorMerge)
    }
    let forceLighter = null
    if (type == 'white') {
        forceLighter = false
    }
    let improvedTextColor = improveContrast(mainColor, pallete.text, 5, forceLighter)
    if (improvedTextColor) {
        pallete.text = improvedTextColor.text
    }
    let improvedHighlightColor = improveContrast(mainColor, pallete.highlight, 6, forceLighter)
    if (improvedHighlightColor) {
        pallete.highlight = improvedHighlightColor.text
    }

    return pallete
}


function normalizeToHEX(color) {
    try {
        if (!color) return color;

        if (color[0] === '#') {
            return color;
        }

        if (color.startsWith('hsl')) {
            var values = color.replace(/hsla?\(/, '').replace(')', '').split(',').map(function(v) {
                return v.trim();
            });
            var h = parseFloat(values[0]);
            var s = parseFloat(values[1]);
            var l = parseFloat(values[2]);
            var rgb = HSLToRGB(h, s, l);
            return RGBToHEX(rgb[0], rgb[1], rgb[2]);
        }

        if (color.startsWith('rgb')) {
            var values = color.replace(/rgba?\(/, '').replace(')', '').split(',').map(function(v) {
                return parseFloat(v.trim());
            });
            return RGBToHEX(values[0], values[1], values[2]);
        }

    } catch (e) {
        console.log('normalizeToHEX error:', e);
    }

    return color;
}


function bestContrastColor(bg, palette) {
    const candidates = [palette.text, palette.main, palette.highlight];
    return candidates.sort((a, b) => getContrastRatio(b, bg) - getContrastRatio(a, bg))[0];
}

function bestContrastColorIgnoringNonColors(bg, palette) {
    let allCandidates = [palette.text, palette.main, palette.highlight];
    // Ignore white or black colors
    candidates = allCandidates.filter(color => color !== '#ffffff' && color !== '#000000');
    if (candidates.length === 0) {
        return null;
    }
    let results = candidates.sort((a, b) => getContrastRatio(b, bg) - getContrastRatio(a, bg))
    let result = results[0];

    // Now check if the result has enough contrast
    if (getContrastRatio(result, bg) < 3) {
        result = allCandidates.sort((a, b) => getContrastRatio(b, bg) - getContrastRatio(a, bg))[0];
        return result
    }
    return result;
}

function bestSaturatedColor(bg, palette) {

    var allCandidates = [palette.text, palette.main, palette.highlight];
    var candidates = allCandidates.filter(function(color) {
        return color !== '#ffffff' && color !== '#000000';
    });

    // Filter out bg
    candidates = candidates.filter(function(color) {
        return color !== bg;
    });

    if (candidates.length === 0) {
        return bg;
    }

    var scored = candidates.map(function(color) {
        var hsl = HEXtoHSL(color);
        var saturation = hsl[1] / 100; // convert to 0–1
        var score = saturation;

        return { color: color, score: score}
    });
    scored.sort(function(a, b) {
        return b.score - a.score;
    });

    return scored[0].color;
}

function bestAdditionalContrastColor(bg, currentFill, palette) {
    const candidates = [palette.text, palette.main, palette.highlight];
    let filteredCandidates = candidates.filter(color => color !== currentFill);
    filteredCandidates = filteredCandidates.sort((a, b) => getContrastRatio(b, bg) - getContrastRatio(a, bg))
    if (filteredCandidates[0] && getContrastRatio(filteredCandidates[0], bg) < 2) {
        return null;
    }
    return filteredCandidates[0];
}

function createAlternativeColor(color, direction, palette) {
    var CHANGE_LIGHTNESS = 10;
    var hsl = HEXtoHSL(color);
    var h = hsl[0];
    var s = hsl[1];
    var l = hsl[2];

    // Handle fully white with no hue (s=0, l=100)
    if (s === 0 && l === 100 && palette) {
        var candidates = ['main', 'text', 'highlight'];
        var mostSaturated = null;
        var maxSaturation = -1;

        for (var i = 0; i < candidates.length; i++) {
            var key = candidates[i];
            if (palette[key]) {
                var hslCandidate = HEXtoHSL(palette[key]);
                if (hslCandidate[1] > maxSaturation) {
                    maxSaturation = hslCandidate[1];
                    mostSaturated = hslCandidate;
                }
            }
        }

        if (mostSaturated) {
            return HSLtoHEX([mostSaturated[0], 40, 89]);
        }
    }

    if (direction === 'light') {
        if (l >= 100 - CHANGE_LIGHTNESS) {
            l = l - CHANGE_LIGHTNESS;
        } else {
            l = l + CHANGE_LIGHTNESS;
        }
    } else if (direction === 'dark') {
        if (l <= CHANGE_LIGHTNESS) {
            l = l + CHANGE_LIGHTNESS;
        } else {
            l = l - CHANGE_LIGHTNESS;
        }
    } else if (typeof direction === 'verylight') {
        l = 95;
    } else if (typeof direction === 'verydark') {
        l = 5;
    }

    // Clamp lightness
    if (l < 0) l = 0;
    if (l > 100) l = 100;

    return HSLtoHEX([h, s, l]);
}

function bestFillWithContrast(bg, palette, params) {
    if (params?.paintConfig?.color && ['highlight'].includes(params.paintConfig.color)) {
        return bestContrastColorIgnoringNonColors(bg, palette);
    }
    if (params?.paintConfig?.color == 'white') {
        return '#ffffff';
    }
    if (params?.paintConfig?.color && ['verylight', 'light', 'dark', 'verydark'].includes(params.paintConfig.color)) {
        return createAlternativeColor(bg, params.paintConfig.color, palette)
    }
    let {preventMain, sameAsParent, currentColor, mapConsistentReplacement, includeAll} = params || {};
    if (currentColor && mapConsistentReplacement?.[bg]?.[currentColor]) {
        return mapConsistentReplacement[bg][currentColor];
    }
    if (sameAsParent) {
        return bg
    }
    let indexContrast = mapConsistentReplacement ? Object.keys(mapConsistentReplacement[bg] || {}).length : 0;
    // Text is not include by default because otherwise highlight would not be used)
    let candidates = [palette.main, palette.highlight];
    if (indexContrast >= 1 || includeAll) {
        candidates.push(palette.text);
    }
    if (preventMain) {
        candidates = candidates.filter(color => color !== palette.main);
        if (candidates.indexOf(palette.text) === -1) {
            // If text is not included, add it
            candidates.push(palette.text);
        }
    }
    candidates = candidates.sort((a, b) => getContrastRatio(b, bg) - getContrastRatio(a, bg))

    // Try to find non repetive bg color
    let filteredCandidates = candidates
    if (indexContrast > 0) {
        let previousUsedCandidates = Object.values(mapConsistentReplacement[bg] || {})
        filteredCandidates = candidates.filter(color => {
            return !previousUsedCandidates.includes(color) && getContrastRatio(color, bg) > 1;
        });
        if (filteredCandidates.length === 0) {
            filteredCandidates = candidates;
        }
    }
    let result = filteredCandidates[0];
    if (!mapConsistentReplacement) {
        mapConsistentReplacement = {};
    }
    if (!mapConsistentReplacement[bg]) {
        mapConsistentReplacement[bg] = {};
    }
    if (currentColor) {
        mapConsistentReplacement[bg][currentColor] = result;
    }
    return result;
}

function topologicalSort(graph) {
    const visited = new Set();
    const result = [];

    function visit(node) {
        if (visited.has(node)) return;
        visited.add(node);

        const parent = graph[node.toString()]?.id
        if (!parent) {
            console.warn(`Node ${node} has no parent`);
        }
        if (parent && parent !== "main_bg") {
            visit(String(parent)); // enforce string keys
        }

        result.push(node);
    }

    Object.keys(graph).forEach(visit);
    return result;
}

function extractSvgColors(svgString) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(svgString, "image/svg+xml");
    // If error, try to encapulate the SVG string
    let needEncapsulation = false;
    if (doc.querySelector('parsererror')) {
        svgString = `<svg xmlns="http://www.w3.org/2000/svg">${svgString}</svg>`;
        doc = parser.parseFromString(svgString, "image/svg+xml");
        needEncapsulation = true;
        if (doc.querySelector('parsererror')) {
            throw new Error("Failed to parse SVG string");
        }
    }
    var svgEl = doc.documentElement;

    var colorMap = {};
    var internalIds = [];
    var externalIds = [];

    var colorToVar = {
        internal: {},
        external: {}
    };

    function isHexOrRgb(value) {
        return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value) ||
               /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/.test(value) ||
               /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[\d.]+\s*\)$/.test(value);
    }

    function assignVar(value, type) {
        value = value.trim().toLowerCase();
        if ((value === 'none') || (value === 'transparent') || value === '') {
            return false
        }

        if (!colorToVar[type][value] && isHexOrRgb(value)) {
            var index = type === 'internal' ? internalIds.length + 1 : externalIds.length + 1;
            var varName = type === 'internal' ? ('$CINT' + index + '$') : ('$CEXT' + index + '$');
            colorMap[varName] = value;
            colorToVar[type][value] = varName;
            if (type === 'internal') internalIds.push(varName);
            else externalIds.push(varName);
        }
        return true
    }

    function processStyle(styleStr, type) {
        return styleStr.replace(/(fill|stroke|stop-color)\s*:\s*([^;]+)(?=;|$)/gi, function(match, prop, val) {
            var value = val.trim();

            // Skip if the value is a url(...)
            if (/^url\(\s*#?.*?\)$/i.test(value)) {
                return match;
            }
            if (!assignVar(value, type)) {
                // If the value is not a color variable, return it as is
                return match;
            }
            var variable = colorToVar[type][value];
            if (!variable) {
                // If the value is not a color variable, return it as is
                return match;
            }
            return prop + ": " + variable;
        });
    }

    function processElement(el) {
        var isExternal = el === svgEl;
        var type = isExternal ? 'external' : 'internal';

        if (el.hasAttribute("style")) {
            var updated = processStyle(el.getAttribute("style"), type);
            el.setAttribute("style", updated);
        }

        // fill / stroke / stop-color attributes
        ['fill', 'stroke', 'stop-color'].map(function(attr) {
            if (el.hasAttribute(attr)) {
                var value = el.getAttribute(attr).trim();

                // Skip if the value is a url(...)
                if (/^url\(\s*#?.*?\)$/i.test(value)) {
                    console.warn(`Skipping ${attr} with url value: ${value}`);
                    return;
                }
                if (!assignVar(value, type)) {
                    // If the value is not a color variable, return it as is
                    return;
                }
                var variable = colorToVar[type][value];
                el.setAttribute(attr, variable);
            }
        });
    }

    var elements = doc.querySelectorAll("*");
    for (var i = 0; i < elements.length; i++) {
        processElement(elements[i]);
    }

    // Normalize to hex colors
    for (var key in colorMap) {
        if (colorMap.hasOwnProperty(key)) {
            colorMap[key] = normalizeToHEX(colorMap[key]);
        }
    }

    // Remove black colors
    if (
        externalIds.length > 0 &&
        externalIds.every(id => {
            const color = (colorMap[id] || '').toLowerCase();
            return color === "#000000" || color === "#000";
        })
    ) {
        externalIds.forEach(id => {
            delete colorMap[id];
        });
        externalIds = [];
    }
    let output = new XMLSerializer().serializeToString(doc);
    if (needEncapsulation) {
        // Remove the encapsulating SVG tag
        output = output.replace(/<svg[^>]*>|<\/svg>/g, '');
    }
    return {
        map: colorMap,
        internal: internalIds,
        external: externalIds,
        svg: output
    };
}

function repaintSVG(value, bgColor, palette, mapConsistentReplacement, sameAsParent, paintConfig) {
    let preventMain = false // TODO - review
    let fillColor = bestFillWithContrast(bgColor, palette, {preventMain, mapConsistentReplacement, paintConfig})
    let svgColors = null
    try {
        svgColors = extractSvgColors(value);
    } catch (e) {
        console.error("Failed to extract SVG colors:", e);
        return {
            value: value,
        }
    }

    let mainFillColor = null
    let uniqueColors = new Set(Object.values(svgColors.map));

    function paintInternalAlternating(fillColor) {
        for (var i = 0; i < svgColors.internal.length; i++) {
            var key = svgColors.internal[i];

            if (svgColors.map.hasOwnProperty(key)) {
                if (i === 0) {
                    svgColors.map[key] = fillColor;
                } else if (i % 2 === 1) {
                    svgColors.map[key] = makeColorLighter(fillColor, 10)
                } else {
                    svgColors.map[key] = makeColorDarker(fillColor, 10)
                }
            }
        }
    }
    if (uniqueColors.size === 1) {
        let fillColor = bestFillWithContrast(bgColor, palette, {
            currentColor: Array.from(uniqueColors)[0],
            preventMain,
            mapConsistentReplacement,
        })
        // Same as parent
        if (sameAsParent && bgColor) {
            fillColor = bgColor;
        }
        // Replace with the same color
        for (let key in svgColors.map) {
            if (svgColors.map.hasOwnProperty(key)) {
                svgColors.map[key] = fillColor;
            }
        }
        mainFillColor = fillColor;
    }
    // 1 external and multiple internal colors
    else if (uniqueColors.size > 1 && svgColors.external.length == 1) {
        fillColor = bestContrastColor(bgColor, palette)
        // Same as parent
        if (sameAsParent && bgColor) {
            fillColor = bgColor;
        }
        // Replace only external with fillColor
        for (let key of svgColors.external) {
            if (svgColors.map.hasOwnProperty(key)) {
                svgColors.map[key] = fillColor;
            }
        }
        let internalNewFillColor = bestContrastColor(fillColor, palette);
        // Replace internal colors with new fill color
        paintInternalAlternating(internalNewFillColor);
        mainFillColor = internalNewFillColor;
    } else if (uniqueColors.size > 1 && svgColors.external.length == 0) {
        // Same as parent
        if (sameAsParent && bgColor) {
            fillColor = bgColor;
        }
        paintInternalAlternating(fillColor);
        mainFillColor = fillColor;
    }

    function escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    // Replace variables in SVG content
    let newSVG = svgColors.svg;
    for (let key in svgColors.map) {
        let color = svgColors.map[key];
        if (color) {
            newSVG = newSVG.replace(new RegExp(escapeRegExp(key), 'g'), color);
        }
    }

    return {
        value: newSVG,
        fillColor: mainFillColor,
    }
}

function slidePalette(palette, index) {
    const keys = Object.keys(palette);
    const values = Object.values(palette);
    const len = values.length;

    // Slide the values by the index (circularly)
    const rotatedValues = values.map((_, i) => values[(i - index + len) % len]);

    // Rebuild the palette with the rotated values
    const newPalette = {};
    keys.forEach((key, i) => {
        newPalette[key] = rotatedValues[i];
    });

    return newPalette;
}

function repaint(layout, paintStructure, palette, paintConfig) {
    if (!paintStructure || !paintStructure?.map || !layout || !palette) {
        console.warn("Invalid parameters for repaint function");
        return;
    }
    if (!paintStructure?.enabled) {
        return;
    }
    const BG_INDEX = 1
    const INDEX_MAIN_ELEMENTS = 2;
    palette = JSON.parse(JSON.stringify(palette));
    let structureMap = paintStructure.map;
    let sortedIds = topologicalSort(structureMap);
    palette = slidePalette(palette, paintConfig?.index || 0);

    // Prevent dark as main bg
    let mainLuminance = getHEXRelativeLuminance(palette.main);
    let mainHSL = HEXtoHSL(palette.main);
    if (mainLuminance < 0.2 && mainHSL[1] < 40) {
        let currentMain = palette.main;
        palette.main = palette.text;
        palette.text = currentMain;
    }
    let mainFillColor = palette.main;
    function mapAllElementsByPId(elements, mapPaintId) {
        if (!elements || !Array.isArray(elements)) return;

        for (var i = 0; i < elements.length; i++) {
            var el = elements[i];

            if (el.pId) {
                mapPaintId[el.pId.toString()] = el;
            }

            if (el.elements && Array.isArray(el.elements) && el.elements.length) {
                // recursive call for children
                mapAllElementsByPId(el.elements, mapPaintId);
            }
        }
    }
    function paintMain(elements, paingConfig) {
        if (paingConfig['main_bg']?.color) {
            try {
                mainFillColor = normalizeToHEX(paingConfig['main_bg'].color)
                return
            } catch (e) {
                console.warn("Failed to normalize main fill color:", e);
            }
        }
        // Repaint main
        for (const el of elements) {
            const isShape = ["rect"].includes(el.type);
            const isSVG = el.type === "svg"
            if (isShape) {
                fillColor = mainFillColor;
                el.computed.background = fillColor;
            } else if (isSVG) {
                fillColor = mainFillColor;
                el.value = el.value.replace(/#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})/g, fillColor);
            }
        }
    }
    paintMain(layout.elements[BG_INDEX].elements, structureMap);
    let mainElements = layout.elements[INDEX_MAIN_ELEMENTS].elements;
    let bgElements = layout.elements[BG_INDEX]?.elements || [];
    let mapPaintId = {}
    mapAllElementsByPId(mainElements, mapPaintId);

    for (const el of bgElements) {
        if (el.pId) {
            mapPaintId[el.pId.toString()] = el
        }
    }
    for (const el of layout.elements) {
        if (el.type != 'container' && !mapPaintId[el.pId]) {
            mapPaintId[el.pId.toString()] = el
        }
    }

    const painted = { "main_bg": mainFillColor }
    const mapConsistentReplacement = {}

    function getAdditionalTextColor(base, palette) {
        let candidateColor = bestContrastColorIgnoringNonColors(base, palette);
        let additionalColor = null;
        if (candidateColor) {
            let hsl = HEXtoHSL(candidateColor);
            additionalColor = HSLtoHEX([hsl[0], 100, 50]);
        }
        return additionalColor;
    }
    let repaintedMap = {}
    for (const id of sortedIds) {
        // TODO - use id
        const el = mapPaintId[id.toString()];
        if (!el || !el.computed) continue;

        const paintConfig = structureMap[id]
        const underId = structureMap[id]?.id
        if (!underId) {
            console.warn(`Element with id ${id} has no underId in structureMap`);
            continue;
        }
        let sameAsParent = structureMap[id].sameAs;
        // TODO - hardcoded this rule, improve
        const preventMain = structureMap[id].area < 0.8 && underId != "main_bg";
        const bgColor = painted[underId] || mainFillColor;
        id == DEBUG_ID && console.log(`\nPainting element with id ${DEBUG_ID}`, 'underId', underId, 'paintedUnder', painted[underId], 'mainColor', mainFillColor);

        const isText = ["p", "h1", "span"].includes(el.type);
        const isIcon = el.type === "fa";
        const isShape = [ "rect"].includes(el.type);
        const isSVG = el.type === "svg"
        const isImage = el.type === "image";
        const isPaintableImage = isImage;
        const isTransparentBG = el?.computed?.background && el?.computed?.background?.indexOf('rgba(0, 0, 0, 0)') !== -1
        let fillColor = null

        function paintBG(bgColor, el) {
            let fillColor = bestFillWithContrast(bgColor, palette, {
                currentColor: el.computed.background,
                preventMain,
                sameAsParent,
                mapConsistentReplacement,
                paintConfig,
            })
            if (paintConfig?.color == 'highlight') {
                fillColor = getAdditionalTextColor(bgColor, palette);
                additionalColor = null
            }
            el.computed.background = fillColor;
            painted[id] = fillColor;
            return fillColor;
        }
        if (isText || isIcon) {
            let currentTextBG = bgColor
            // If also have background, paint it first
            id == DEBUG_ID && console.log(`Painting text/icon with id ${DEBUG_ID}`, el, 'bgColor', bgColor)
            if (el.computed.background) {
                el == DEBUG_ID && console.log(`====> Painting background for text/icon with id ${DEBUG_ID}`);
                let bgFill = paintBG(currentTextBG, el);
                if (bgFill) {
                    currentTextBG = bgFill;
                }
                id == DEBUG_ID && console.log(`====> New currentTextBG for text/icon with id ${DEBUG_ID}`, currentTextBG);
            }
            let currentColor = el.computed.color
            id == DEBUG_ID && console.log(`Current color for text/icon with id ${DEBUG_ID}`, currentColor);
            let fillColor = bestContrastColor(currentTextBG, palette);
            let additionalColor = bestAdditionalContrastColor(currentTextBG, fillColor, palette);
            id == DEBUG_ID && console.log(`Best fillColor and additionalColor for text/icon with id ${DEBUG_ID}`, currentTextBG, fillColor, additionalColor);
            if (currentColor == 'highlight') {
                fillColor = additionalColor
            }
            if (paintConfig?.color == 'light') {
                fillColor = '#ffffff';
                additionalColor = getAdditionalTextColor('#000000', palette);
            } else if (paintConfig?.color == 'dark') {
                fillColor = bestContrastColor('#ffffff', palette);
                let ratio = getContrastRatio(fillColor, '#ffffff')
                // Add darker version if contrast is too low
                if (ratio < 5) {
                    let hsl = HEXtoHSL(fillColor);
                    hsl[2] = 15
                    fillColor = HSLtoHEX(hsl);
                }
                additionalColor = getAdditionalTextColor('#ffffff', palette);
            } else if (paintConfig?.color == 'light-highlight') {
                fillColor = getAdditionalTextColor('#000000', palette);
                additionalColor = null
            } else if (paintConfig?.color == 'dark-highlight') {
                fillColor = getAdditionalTextColor('#ffffff', palette);
                additionalColor = null
            } else if (paintConfig?.color == 'highlight') {
                fillColor = getAdditionalTextColor(currentTextBG, palette);
                additionalColor = null
            }
            id == DEBUG_ID && console.log(`Final fillColor and additionalColor for text/icon with id ${DEBUG_ID}`, fillColor, additionalColor);
            el.computed.color = fillColor;
            if (el.value && additionalColor) {
                el.value = el.value.replace(/color\s*:\s*([^;'"]+)\s*;?/gi, function (match, color) {
                    return 'color: ' + additionalColor + ';';
                });
            }
            repaintedMap[id] = {
                main: currentTextBG,
                text: fillColor,
                highlight: additionalColor || fillColor,
            }
            id == DEBUG_ID && console.log(`Painted text/icon with id ${DEBUG_ID}:`, repaintedMap[id], fillColor);
            painted[id] = fillColor;
        } else if (isShape && !isTransparentBG) {
            paintBG(bgColor, el);
        } else if ((isImage && el.content) || isSVG) {
            let key = isSVG ? 'value': 'content';
            fillColor = bestFillWithContrast(bgColor, palette, {
                preventMain,
                sameAsParent,
                mapConsistentReplacement,
            })
            let result = repaintSVG(el[key], bgColor, palette, mapConsistentReplacement, sameAsParent, paintConfig)
            if (result?.value) {
                el[key] = result?.value;
            }
            painted[id] = result?.fillColor || fillColor;
        } else if (isPaintableImage) {
            try {
                let mainImageColor = el.imageFill
                if (!mainImageColor && el?.pallete?.[0].hex) {
                    mainImageColor = el.pallete[0].hex;
                }
                fillColor = bestFillWithContrast(bgColor, palette, {
                    currentColor: el.imageFill,
                    preventMain,
                    sameAsParent,
                    mapConsistentReplacement,
                    paintConfig,
                })

                let filter = null
                if (paintConfig?.color == 'rotate') {
                    // Rotate forces highlight color
                    fillColor = bestSaturatedColor(bgColor, palette);
                    el.computed['--tint-color'] = fillColor;
                    el.computed['--image-url'] = 'url(' + el.src + ')';
                } else {
                    let { red, green, blue } = hexToRgb(fillColor);
                    let solution = new Solver(new Color(red, green, blue)).solve()
                    filter = solution.filter.replace('filter: ', '').replace(';', '')
                }
                el.filter = filter;
                painted[id] = fillColor;
            } catch (e) {
                console.error(`Failed to paint image with id ${id}:`, e);
                continue;
            }
        }
    }

    return repaintedMap
}